import { forwardRef, InputHTMLAttributes, useRef, useEffect, useState } from 'react'
import { clsx } from 'clsx'

interface SliderProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  label?: string
  leftLabel?: string
  rightLabel?: string
  showValue?: boolean
  step?: number
  min?: number
  max?: number
  disabled?: boolean
  value?: number
  onChange?: (value: number) => void
  thumbColor?: string
}

export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  ({ className, label, leftLabel, rightLabel, showValue = true, step = 1, min = 0, max = 100, disabled, value, onChange, thumbColor, ...props }, ref) => {
    const trackRef = useRef<HTMLDivElement>(null)
    const [isDragging, setIsDragging] = useState(false)

    // Calculate thumb position percentage
    useEffect(() => {
      if (value !== undefined) {
        // Percentage is calculated inline in render
      }
    }, [value, min, max])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = Math.round(parseFloat(e.target.value) / step) * step
      const clampedValue = Math.max(min, Math.min(max, newValue))
      onChange?.(clampedValue)
    }

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      if (disabled) return
      setIsDragging(true)
      updateValueFromPosition(e.clientX)
    }

    const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
      if (disabled) return
      setIsDragging(true)
      updateValueFromPosition(e.touches[0].clientX)
    }

    const updateValueFromPosition = (clientX: number) => {
      if (!trackRef.current) return
      const rect = trackRef.current.getBoundingClientRect()
      const percentage = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100))
      const newValue = Math.round((min + (percentage / 100) * (max - min)) / step) * step
      const clampedValue = Math.max(min, Math.min(max, newValue))
      onChange?.(clampedValue)
    }

    useEffect(() => {
      if (isDragging) {
        const handleMove = (e: MouseEvent | TouchEvent) => {
          const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
          updateValueFromPosition(clientX)
        }
        const handleUp = () => setIsDragging(false)

        window.addEventListener('mousemove', handleMove)
        window.addEventListener('mouseup', handleUp)
        window.addEventListener('touchmove', handleMove, { passive: true })
        window.addEventListener('touchend', handleUp)

        return () => {
          window.removeEventListener('mousemove', handleMove)
          window.removeEventListener('mouseup', handleUp)
          window.removeEventListener('touchmove', handleMove)
          window.removeEventListener('touchend', handleUp)
        }
      }
    }, [isDragging])

    const percentage = value !== undefined ? ((value - min) / (max - min)) * 100 : 0

    return (
      <div className={clsx('w-full', className)}>
        {(label || leftLabel || rightLabel) && (
          <div className="flex items-center justify-between mb-3">
            {label && <label className="text-sm font-medium text-surface-700 dark:text-surface-300">{label}</label>}
            <div className="flex items-center gap-2 w-full">
              {leftLabel && <span className="text-xs text-surface-500 dark:text-surface-400 whitespace-nowrap">{leftLabel}</span>}
              {showValue && value !== undefined && (
                <span className="text-sm font-semibold text-brand-600 dark:text-brand-400 w-12 text-center">
                  {value}
                </span>
              )}
              {rightLabel && <span className="text-xs text-surface-500 dark:text-surface-400 whitespace-nowrap">{rightLabel}</span>}
            </div>
          </div>
        )}
        
        <div
          ref={trackRef}
          className="relative h-2 bg-surface-200 dark:bg-surface-700 rounded-full cursor-pointer touch-none"
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={value ?? 0}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
        >
          {/* Filled track */}
          <div
            className="absolute top-0 left-0 h-full rounded-full transition-all duration-100"
            style={{ width: `${percentage}%`, background: `linear-gradient(to right, ${thumbColor || 'var(--brand-500)'}, ${thumbColor || 'var(--brand-600)'})` }}
          />
          
          {/* Thumb */}
          <div
            className={clsx(
              'absolute top-1/2 w-6 h-6 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full border-2 shadow-lg transition-all duration-100 pointer-events-none',
              isDragging && 'scale-110',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
            style={{ left: `${percentage}%`, borderColor: thumbColor || 'var(--brand-500)' }}
            aria-hidden="true"
          >
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: thumbColor || 'var(--brand-500)' }} />
            </div>
          </div>
        </div>

        {/* Hidden native input for accessibility */}
        <input
          ref={ref}
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={handleChange}
          className="sr-only"
          disabled={disabled}
          {...props}
        />
      </div>
    )
  }
)

Slider.displayName = 'Slider'